/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import classNames from 'classnames';
import PropTypes from "prop-types";
import React from 'react';
import { EuiStepNumber } from './step_number';
import { useI18nCompleteStep, useI18nCurrentStep, useI18nDisabledStep, useI18nIncompleteStep, useI18nStep, useI18nWarningStep } from './step_strings';
export var EuiStepHorizontal = ({
  className,
  step = 1,
  title,
  onClick,
  disabled,
  status = 'incomplete',
  ...rest
}) => {
  const buttonTitle = useI18nStep({
    number: step,
    title
  });
  const completeTitle = useI18nCompleteStep({
    number: step,
    title
  });
  const disabledTitle = useI18nDisabledStep({
    number: step,
    title
  });
  const incompleteTitle = useI18nIncompleteStep({
    number: step,
    title
  });
  const warningTitle = useI18nWarningStep({
    number: step,
    title
  });
  const currentTitle = useI18nCurrentStep({
    number: step,
    title
  });
  if (disabled) status = 'disabled';
  const classes = classNames('euiStepHorizontal', className, {
    'euiStepHorizontal-isSelected': status === 'current',
    'euiStepHorizontal-isComplete': status === 'complete',
    'euiStepHorizontal-isIncomplete': status === 'incomplete',
    'euiStepHorizontal-isDisabled': status === 'disabled'
  });
  let stepTitle = buttonTitle;
  if (status === 'disabled') stepTitle = disabledTitle;
  if (status === 'complete') stepTitle = completeTitle;
  if (status === 'incomplete') stepTitle = incompleteTitle;
  if (status === 'warning') stepTitle = warningTitle;
  if (status === 'current') stepTitle = currentTitle;

  const onStepClick = event => {
    if (!disabled) onClick(event);
  };

  return <button className={classes} title={stepTitle} onClick={onStepClick} disabled={disabled} {...rest}>
      <EuiStepNumber className="euiStepHorizontal__number" status={status} number={step} />

      <span className="euiStepHorizontal__title">{title}</span>
    </button>;
};
EuiStepHorizontal.propTypes = {
  onClick: PropTypes.func.isRequired,

  /**
     * Makes the whole step button disabled.
     */
  disabled: PropTypes.bool,

  /**
     * The number of the step in the list of steps
     */
  step: PropTypes.number,
  title: PropTypes.string,

  /**
     * Visual representation of the step number indicator.
     * May replace the number provided in props.step with alternate styling.
     * The `isSelected`, `isComplete`, and `disabled` props will override these.
     */
  status: PropTypes.any,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiStepHorizontal.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/steps/step_horizontal.tsx',
    description: '',
    displayName: 'EuiStepHorizontal',
    methods: [],
    props: {
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: {
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        }],
        required: true,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      disabled: {
        defaultValue: null,
        description: 'Makes the whole step button disabled.',
        name: 'disabled',
        parent: {
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      step: {
        defaultValue: {
          value: '1'
        },
        description: 'The number of the step in the list of steps',
        name: 'step',
        parent: {
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      title: {
        defaultValue: null,
        description: '',
        name: 'title',
        parent: {
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      status: {
        defaultValue: {
          value: 'incomplete'
        },
        description: 'Visual representation of the step number indicator.\n' + 'May replace the number provided in props.step with alternate styling.\n' + 'The `isSelected`, `isComplete`, and `disabled` props will override these.',
        name: 'status',
        parent: {
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        },
        declarations: [{
          fileName: 'app/src/components/steps/step_horizontal.tsx',
          name: 'EuiStepHorizontalProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"danger" | "warning" | "disabled" | "loading" | "incomplete" | "complete" | "current"',
          value: [{
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"disabled"'
          }, {
            value: '"loading"'
          }, {
            value: '"incomplete"'
          }, {
            value: '"complete"'
          }, {
            value: '"current"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiStepHorizontalProps', 'HTMLAttributes', 'ButtonHTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps']
  };
} catch (e) {}